/**
 * Cleanup sku url to get the proper sku.
 *
 * @param urlSku the urlSku
 * @return the proper sku
 */
export const urlSkuToSku = (urlSku: string): string =>
    urlSku
        // replace all p by '-'
        .replace(/p/g, "-")
        // replace all 'v' by spaces
        .replace(/v/g, " ");

/**
 * Get sku from product url.
 * Ex: Gets H117640B 00 from /product/niloticus-eclat-ring-medium-model-H117640Bv00/
 * Must also handle format like /product/H117640Bv00
 * @param productUrl the urlSku
 * @return the proper sku
 */
export const skuFromUrl = (productUrl: string | undefined | null): string => {
    if (!productUrl) {
        return "";
    }
    // Remove hash and query.
    const clean = String(productUrl).split("#")[0].split("?")[0];

    // Find "product" occurence.
    const needle = "/product/";
    const index = clean.toLowerCase().lastIndexOf(needle);
    if (index === -1) {
        return "";
    }

    // Find next part.
    let rest = clean.slice(index + needle.length);
    if (!rest) {
        return "";
    }

    // Remove multiple "/" at end.
    rest = rest.replace(/\/+$/, "");

    // If any "-" found, sku is the element following the last "-".
    // Otherwise, sku is the whole element following "/product/".
    const lastDash = rest.lastIndexOf("-");
    const sku = lastDash >= 0 ? rest.slice(lastDash + 1) : rest;

    return urlSkuToSku(sku);
};

/**
 * Get complete sku from product url, including size.
 *
 * @param productUrl the product url
 * @param size the product size
 * @return the complete sku
 */
export const skuFromUrlAndSize = (productUrl: string, size: string): string =>
    skuFromUrl(productUrl) + size;

/**
 * Format sku to get the proper url sku.
 *
 * @param sku the sku
 * @return the proper url sku
 */
export const skuToUrlSku = (sku: string): string =>
    sku
        // replace all '-' by p
        .replace(/-/g, "p")
        // replace all spaces by 'v'
        .replace(/ /g, "v");

/**
 * Format sku to get the proper url sku.
 *
 * @param sku the sku
 * @return the proper url sku
 */
export const skuToId = (sku: string): string =>
    sku
        // replace all spaces by '_'
        .replace(/ /g, "_");

/**
 * formatting product SKU for Product locator services
 */
export const formatSkuForPLServices = (urlSku: string): string =>
    urlSku.replace(/[\s_pv]/g, "-");

/**
 * Format the SKU to the one expected by scene7 :
 * no v but spaces, no first char, no size indication.
 */
export const formatSkuForImage = (fullSKU: string): string => {
    // sku in scene 7 does not have fist char and spaces is valid
    let cleanSKU = fullSKU.replace(/^.(.*)/, "$1").replace("v", " ");
    cleanSKU = cleanSKU.slice(0, 10);
    cleanSKU = cleanSKU.replace("/", ".");
    cleanSKU = cleanSKU.includes("_") ? cleanSKU.split("_")[0] : cleanSKU;

    return cleanSKU;
};

export const isSimpleProduct = (sku: string): boolean =>
    !isBeltkit(sku) &&
    !isAppleWatch(sku) &&
    !isDoubleFragance(sku) &&
    !isGiftSet(sku) &&
    !isLook(sku) &&
    !isBikini(sku) &&
    !isDoubleObject(sku) &&
    !isBundle(sku);

export const isBeltkit = (sku: string): boolean => {
    const regex = /^U_BELT/;
    return regex.test(sku);
};

export const isAppleWatch = (sku: string): boolean => {
    const regex = /_KIT|\dAWH/;
    return regex.test(sku);
};

export const isDoubleFragance = (sku: string): boolean => {
    const regex = /^V2(?:HERMESSENCE|LOCKSGIFT|LEATHERCASE)/;
    return regex.test(sku);
};

export const isGiftSet = (sku: string): boolean => {
    const regex =
        /^V(3|4|8|12)(?:COLOGNEGIFT|HERMESSENCE|NOMADEGIFT|SOAPGIFTSET|.*BEAUTYSET.*)/;
    return regex.test(sku);
};

export const isLook = (sku: string): boolean => {
    const regex = /H([AP])\d\d(LKDP|LKDC).*HOMME|D([AP])\d\d(LOOK|LKMB).*FEMME/;
    return regex.test(sku);
};

export const isBikini = (sku: string | undefined): boolean => {
    if (!sku) {
        return false;
    }
    const regex = /D(P|A)\d\dSETB\d\dFEMME/;
    return regex.test(sku);
};

export const isDoubleObject = (sku: string): boolean => {
    const regex = /[\dA-Z](P|A)\d{2}SET[A-Z]\d{2}[\dA-Z]+DBO/;
    return regex.test(sku);
};

export const isBundle = (sku: string): boolean => {
    const regex = /VP\d{2}SETD?\d{2}BUNDLE/;
    return regex.test(sku);
};

export const skuToGridProductId = (sku: string): string =>
    `product-item-meta-link-${skuToId(sku)}`;
